import React, { useMemo, useState } from 'react';
import { IconButton, Box, Stack, Typography } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import { useDispatch, useSelector } from 'react-redux';
import { DateRangeEntry } from 'shared/src/components/dialogEntries/DateRangeEntry';
import { MapTagSelect } from 'shared/src/components/MapTagSelect';
import { TzViewTBSelect } from 'shared/src/components/TzViewTBSelect';
import { convertRangeDatesToIsoStrs, getDateRangeToday, getTimeZoneIana } from 'shared/src/utils/dateTimeUtils';
import { useOrgType } from 'shared/src/hooks/useOrgType';
import { cellFormatDateTime, PAGINATION_DEF } from 'shared/src/utils/tablesCommonFormats';
import CustomAccordion from 'shared/src/components/responsive/CustomAccordion';
import { useGetInstalledAssets } from 'shared/src/portalQueryApi/queryAssetsApi';
import DownloadCsv from 'shared/src/apis/apiCsv';

const InstallationReportMobilePage = (props) => {
  const { title, entityName } = props;
  const dispatch = useDispatch();
  const parentOrgId = useSelector((state) => state.user.parentOrgId);
  const { timezoneIana } = useOrgType();
  const [tzView, setTzView] = useState('Fleet');
  const [dateRange, setDateRange] = useState(() => getDateRangeToday(timezoneIana));
  const [tags, setTags] = useState([]);

  const tzInfo = useMemo(() => {
    return tzView === 'Local' ? { iana: getTimeZoneIana(), local: true } : { iana: timezoneIana, local: false };
  }, [tzView, timezoneIana]);
  const cmnUseQueryParams = { pagination: PAGINATION_DEF, dateRange: convertRangeDatesToIsoStrs(dateRange), tags, fleetId: parentOrgId };
  const { data: { count, rows: dataArr = [] } = {}, isError, isFetching, isLoading } = useGetInstalledAssets(cmnUseQueryParams);
  const handleChangeMutliValueEl = (e) => {
    const {
      target: { value },
    } = e;
    const val = typeof value === 'string' ? value.split(',') : value;
    setTags(val);
  };

  const handleDateRangeFilterChange = (range) => {
    setDateRange(range);
  };

  const handleDownload = () => {
    DownloadCsv.apiInstalledAssetsGetCsv({ entityName, dateRange });
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'entityId',
        header: 'IMEI',
        filterVariant: 'autocomplete',
      },
      {
        accessorKey: 'entityName',
        header: 'Vehicle',
        filterVariant: 'autocomplete',
      },
      {
        accessorFn: (row) => new Date(row.installationStatus?.installationDate),
        id: 'installation_date',
        header: 'Installation Date',
        Cell: cellFormatDateTime,
        filterVariant: 'date',
        enableColumnFilterModes: false,
        // filterFn: (row, id, filterValue) => filterFnDateEq(row, id, filterValue, tzInfo),
      },
      {
        accessorFn: (row) => row.installationStatus?.installedBy,
        id: 'installed_by',
        header: 'Installed By',
        filterVariant: 'autocomplete',
      },
      {
        accessorFn: (row) => row.installationImages ?? [],
        id: 'installation_images',
        header: 'Installation Images',
        Cell: ({ cell }) =>
          cell?.length ? (
            <div
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => handleClickOpen(cell.row.original)}
            >{`${cell.length} images`}</div>
          ) : null,
      },
    ],
    [tzInfo],
  );
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: '10px', px: '16px', mb: '16px' }}>
        <IconButton
          aria-label='Open sidemenu'
          onClick={() => {
            dispatch({ type: 'portal/set', ResponsivesidebarToggle: true });
          }}
          sx={{ p: 0 }}
        >
          <MenuIcon sx={{ color: (theme) => theme.palette.primary.main }} />
        </IconButton>
        <Stack direction='row' spacing={1}>
          <IconButton onClick={handleDownload} sx={{ padding: '0px' }}>
            <FileDownloadOutlinedIcon />
          </IconButton>
        </Stack>
      </Box>
      <Box sx={{ px: '16px' }}>
        <Typography variant='h3b' sx={{ color: (theme) => theme.palette.gray90 }}>
          Installations
        </Typography>
        <Stack spacing={1} sx={{ pt: '8px' }}>
          <Stack direction='row' spacing={1}>
            <DateRangeEntry value={dateRange} onChange={handleDateRangeFilterChange} compact={true} timezoneIana={tzInfo.iana} />
            <MapTagSelect name='tags' value={tags} handleChange={handleChangeMutliValueEl} filterByUserTags={true} format='id' />
          </Stack>
          <TzViewTBSelect name='tzselect' value={tzView} onChange={setTzView} />
        </Stack>
        <Box>
          {dataArr.map((asset, index) => (
            <CustomAccordion
              key={`${asset.entityName}-${index}`}
              data={asset}
              columns={columns}
              entityName={entityName}
              editEnabled={false}
              deleteEnabled={false}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default InstallationReportMobilePage;
